import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOrganizationInvitations,
  getOrganizationMembers,
  getUser,
  IInviteUsersRequest,
  inviteUsersToOrganization,
  IRemindInvitationRequest,
  IRemoveUserFromOrganizationRequest,
  IRevokeInvitationRequest,
  IUpdateDefaultOrganizationRequest,
  remindInvitation,
  removeUserFromOrganization,
  revokeInvitation,
  updateDefaultOrganization,
  updateUser,
} from '../../api/user';

export const useOrganizationMembers = (organizationId: string) =>
  useQuery({
    queryKey: ['organizationMembers', organizationId],
    queryFn: () => getOrganizationMembers(organizationId),
    enabled: !!organizationId,
  });

export const useUpdateUser = () =>
  useMutation({
    mutationFn: (data: {
      userSub: string;
      firstName: string;
      lastName: string;
    }) =>
      updateUser(data.userSub, {
        firstName: data.firstName,
        lastName: data.lastName,
      }),
  });

export const useGetUser = () =>
  useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on network reconnect
  });

export const useInviteUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IInviteUsersRequest) => inviteUsersToOrganization(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['organizationInvitations', variables.organizationId],
      });
    },
  });
};

export const useOrganizationInvitations = (organizationId: string) =>
  useQuery({
    queryKey: ['organizationInvitations', organizationId],
    queryFn: () => getOrganizationInvitations(organizationId),
    enabled: !!organizationId,
  });

export const useRemindInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IRemindInvitationRequest) => remindInvitation(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['organizationInvitations', variables.organizationId],
      });
    },
  });
};

export const useRevokeInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IRevokeInvitationRequest) => revokeInvitation(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['organizationInvitations', variables.organizationId],
      });
    },
  });
};

export const useRemoveUserFromOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IRemoveUserFromOrganizationRequest) =>
      removeUserFromOrganization(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['organizationMembers', variables.organizationId],
      });
    },
  });
};

export const useUpdateDefaultOrganization = () =>
  useMutation({
    mutationFn: (data: IUpdateDefaultOrganizationRequest) =>
      updateDefaultOrganization(data),
  });
